import { GameRuntime } from "@/game/runtime";
import { HeartParticleData } from "@/shared/types/data";
import { RefObject, useEffect, useRef, useState } from "react";
import { useBounds } from "./useBounds";

const BASE_HEART_EMIT_INTERVAL_MS = 120;
const FAST_HEART_EMIT_INTERVAL_MS = 78;
const MAX_VISIBLE_QUEUE = 8;
const OVERFLOW_LABEL_CHUNK = 6;
const MAX_PARTICLES = 36;
const FRAME_MS = 1000 / 60;

export function useSpawnHearts(
  runtime: GameRuntime,
  buttonRef: RefObject<HTMLElement | null>,
) {
  const [particles, setParticles] = useState<HeartParticleData[]>([]);
  const bounds = useBounds(buttonRef);
  const pendingLikesRef = useRef(0);
  const boundsRef = useRef(bounds);
  const particlesRef = useRef<HeartParticleData[]>([]);
  const particleIdRef = useRef(0);

  useEffect(() => {
    boundsRef.current = bounds;
  }, [bounds]);

  const createHeartParticle = (
    x: number,
    y: number,
  ): HeartParticleData => ({
    id: ++particleIdRef.current,
    kind: "heart",
    x,
    y,
    vx: (Math.random() - 0.5) * 2.2,
    vy: -(Math.random() * 2.5 + 2.5),
    life: 0,
    maxLife: 45 + Math.random() * 20,
    drift: (Math.random() - 0.5) * 0.8,
    scale: 0.8 + Math.random() * 0.6,
  });

  const createLabelParticle = (
    x: number,
    y: number,
    amount: number,
  ): HeartParticleData => ({
    id: ++particleIdRef.current,
    kind: "label",
    label: `+${amount}`,
    x: x + (Math.random() - 0.5) * 24,
    y: y - 8,
    vx: (Math.random() - 0.5) * 1.2,
    vy: -(Math.random() * 1.8 + 2.2),
    life: 0,
    maxLife: 50 + Math.random() * 18,
    drift: (Math.random() - 0.5) * 0.6,
    scale: 0.9 + Math.random() * 0.35,
  });

  useEffect(() => {
    const unsubLikesChanged = runtime.on("likesChanged", ({ delta }) => {
      if (delta <= 0) {
        return;
      }

      pendingLikesRef.current += delta;
    });

    return () => {
      unsubLikesChanged?.();
    };
  }, [runtime]);

  useEffect(() => {
    let frameId = 0;
    let lastFrameTime = 0;
    let emitAccumulator = 0;

    const tick = (now: number) => {
      if (lastFrameTime === 0) {
        lastFrameTime = now;
      }

      const deltaMs = Math.min(32, now - lastFrameTime);
      const frameScale = deltaMs / FRAME_MS;
      lastFrameTime = now;

      let nextParticles = particlesRef.current
        .map((particle) => {
          const nextLife = particle.life + frameScale;

          return {
            ...particle,
            life: nextLife,
            x:
              particle.x +
              (particle.vx + Math.sin(nextLife * 0.2) * particle.drift) *
                frameScale,
            y: particle.y + particle.vy * frameScale,
            vy:
              particle.vy +
              (particle.kind === "label" ? 0.015 : 0.05) * frameScale,
          };
        })
        .filter((particle) => particle.life < particle.maxLife);

      const currentBounds = boundsRef.current;
      const pendingLikes = pendingLikesRef.current;

      if (currentBounds && pendingLikes >= 0.6) {
        const emitInterval =
          pendingLikes > MAX_VISIBLE_QUEUE
            ? FAST_HEART_EMIT_INTERVAL_MS
            : BASE_HEART_EMIT_INTERVAL_MS;

        emitAccumulator += deltaMs;

        while (
          emitAccumulator >= emitInterval &&
          pendingLikesRef.current >= 0.6
        ) {
          const x = currentBounds.left + currentBounds.width / 2;
          const y = currentBounds.top + currentBounds.height / 2;

          if (pendingLikesRef.current > MAX_VISIBLE_QUEUE) {
            const overflowAmount = Math.min(
              OVERFLOW_LABEL_CHUNK,
              Math.max(
                2,
                Math.round(pendingLikesRef.current - MAX_VISIBLE_QUEUE + 1),
              ),
            );

            nextParticles.push(createLabelParticle(x, y, overflowAmount));
            pendingLikesRef.current = Math.max(
              MAX_VISIBLE_QUEUE - 1,
              pendingLikesRef.current - overflowAmount,
            );
          }

          nextParticles.push(createHeartParticle(x, y));
          pendingLikesRef.current = Math.max(0, pendingLikesRef.current - 1);
          emitAccumulator -= emitInterval;
        }
      } else {
        emitAccumulator = 0;
      }

      if (nextParticles.length > MAX_PARTICLES) {
        nextParticles = nextParticles.slice(-MAX_PARTICLES);
      }

      particlesRef.current = nextParticles;
      setParticles(nextParticles);
      frameId = window.requestAnimationFrame(tick);
    };

    frameId = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frameId);
  }, []);

  return particles;
}
